import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

import { API_URL } from '../utils/apiUrl';

const CATEGORIES = [
  { name: "Elektronik", disabled: false },
  { name: "Giyim", disabled: false },
  { name: "Kozmetik", disabled: false },
  { name: "Ev Ürünleri", disabled: false },
  { name: "Aksesuar", disabled: false },
  { name: "Kişisel Bakım", disabled: false },
  { name: "Gıda", disabled: false },
  { name: "2. El Araçlar", disabled: true },
  { name: "Diğer", disabled: false },
];

const LEGAL_TEXT = `TestettiK® platformuna yapılan ürün test başvuruları, bağımsız değerlendirme esasına göre yürütülür. Başvuru sahibi, gönderdiği ürünün hiçbir koşulda olumlu sonuç garantisi taşımadığını peşinen kabul eder.

Gönderilen ürün, TestettiK® tarafından belirlenen test kriterlerine göre incelenir ve ortaya çıkan sonuç olumlu ya da olumsuz fark etmeksizin yayınlanabilir. Başvuru yapılması, ürünün onay alacağı, rozet verileceği veya premium görünürlük kazanacağı anlamına gelmez.

TestettiK® tarafından yapılan değerlendirmeler, ilgili ürünün test edildiği tarihteki fiziksel durumu, sunumu, içeriği, performansı ve belirlenen test koşulları esas alınarak hazırlanır. Bu değerlendirmeler genel, evrensel veya süresiz kalite garantisi niteliği taşımaz.

Başvuru sahibi, gönderdiği ürünün test sürecine alınmasını, test sırasında incelenmesini, gerektiğinde açılmasını, sökülmesini, parçalanmasını veya detaylı fiziksel kontrole tabi tutulmasını kabul eder. Ürün test süreci sonunda iade edilmeyebilir.

TestettiK® hiçbir marka veya satıcı ile sonuç değiştirmeye yönelik ticari ortaklık kurmaz. Yapılan ödemeler, olumlu sonuç satın alma amacı taşımaz. Sonuçlar değiştirilemez, gizlenemez veya kaldırılmaz.

Rozet yalnızca TestettiK® tarafından uygun bulunan ürünlere verilir ve 30 gün süreyle geçerlidir. Süre sonunda doğrulama hakkı sona erer.

Havale/EFT ile yapılan ödemelerde doğrulama süresi 24–48 saat sürebilir.

TestettiK® platformunda yayınlanan tüm içerikler fikri mülkiyet kapsamında korunur.

Test süreci başladıktan sonra iptal edilemez ve ücret iadesi yapılmaz.

TestettiK® marka itibarı hakkında yorum yapmaz; yalnızca ürünü değerlendirir.

Başvuru sahibi, marka ve ürün adının TestettiK® tarafından kullanılmasına izin verir.

TestettiK® doğrudan veya dolaylı zararlardan sorumlu tutulamaz.

TestettiK® sonuç odaklı ticari anlaşmalar yapmaz.

ÜRÜNÜN GÖNDERİMİNİ YAPAN MARKA VEYA SATICI, TEST İÇİN GÖNDERDİĞİ ÜRÜN İLE MÜŞTERİLERİNE SUNDUĞU ÜRÜNÜN AYNI OLDUĞUNU BEYAN VE TAAHHÜT EDER. AKSİ DURUMDA DOĞABİLECEK TÜM HUKUKİ SÜREÇLERİN TEK SORUMLUSU OLDUĞUNU KABUL EDER.

Başvuru sahibi tüm şartları okuduğunu ve kabul ettiğini beyan eder.`;

function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '');
  let d = digits;
  if (d.startsWith('0') || d.startsWith('9')) d = d.replace(/^(90|0)/, '');
  if (d.length > 10) d = d.slice(0, 10);
  if (!d.startsWith('5') && d.length > 0) return d.slice(0, 0);
  return d;
}

function rawDigits(formatted) {
  return formatted.replace(/\D/g, '');
}

const SubmissionPage = () => {
  const [formData, setFormData] = useState({
    brand: '', product_name: '', business_name: '', category: '',
    description: '', address: '', city: '', contact_name: '',
    contact_email: '', contact_phone: '',
    kvkk_accepted: false, process_irreversible_accepted: false,
    product_handling_accepted: false, results_final_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const legalContentRef = useRef(null);
  // Ref-based guard against double-submit (survives React re-renders synchronously)
  const inFlightRef = useRef(false);
  // Idempotency key: one per form session — enables backend duplicate protection
  const idempotencyKeyRef = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const [whatsappSettings, setWhatsappSettings] = useState({
    number: '+905325194229',
    message: 'Merhaba, TestettiK® ürün testi gereklilikleri hakkında bilgi almak istiyorum.',
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`).then(({ data }) => {
      if (data.whatsapp_number) {
        setWhatsappSettings({ number: data.whatsapp_number, message: data.whatsapp_message || whatsappSettings.message });
      }
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'contact_phone') {
      const formatted = formatPhoneInput(value);
      setFormData(prev => ({ ...prev, contact_phone: formatted }));
      const digits = rawDigits(formatted);
      if (digits.length > 0 && !digits.startsWith('5')) {
        setPhoneError('Telefon numarası 5 ile başlamalıdır');
      } else if (digits.length === 10 && !(/^5\d{9}$/.test(digits))) {
        setPhoneError('Geçerli bir telefon numarası girin (5XXXXXXXXX)');
      } else {
        setPhoneError('');
      }
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const getWhatsAppUrl = () => {
    const num = whatsappSettings.number.replace(/[^0-9]/g, '');
    // Include reference number so admins can immediately locate the application
    const message = referenceNumber
      ? `Merhaba, TestettiK® ürün test başvurum hakkında bilgi almak istiyorum. Referans numaram: ${referenceNumber}`
      : whatsappSettings.message;
    return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
  };

  const handleLegalScroll = () => {
    const el = legalContentRef.current;
    if (el) {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
      if (atBottom) setScrolledToBottom(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Double-submit guard: ref check runs synchronously (React state may not have updated yet)
    if (inFlightRef.current || loading) return;

    const { kvkk_accepted, process_irreversible_accepted, product_handling_accepted, results_final_accepted } = formData;
    if (!kvkk_accepted || !process_irreversible_accepted || !product_handling_accepted || !results_final_accepted) {
      toast.error('Lütfen tüm onay kutularını işaretleyin.');
      return;
    }
    if (formData.description.length < 50) {
      toast.error('Açıklama en az 50 karakter olmalıdır.');
      return;
    }
    const digits = rawDigits(formData.contact_phone);
    if (digits.length !== 10 || !digits.startsWith('5')) {
      toast.error('Telefon numarası 10 haneli olmalı ve 5 ile başlamalıdır (5XXXXXXXXX)');
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setLoadingMessage('Başvuru gönderiliyor...');

    // After 3s show reassuring message; after 20s abort with clean error
    const slowTimer = setTimeout(() => {
      setLoadingMessage('Başvurunuz işleniyor, lütfen bekleyiniz.');
    }, 3000);
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 20000);

    try {
      const payload = {
        ...formData,
        contact_phone: digits,
        idempotency_key: idempotencyKeyRef.current,
      };
      const { data } = await axios.post(`${API_URL}/api/submissions`, payload, { signal: controller.signal });
      setReferenceNumber(data.reference_number);
      setSubmitted(true);
      toast.success('Başvurunuz başarıyla alındı!');
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        toast.error('Başvuru gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.');
      } else {
        // Show backend detail only for validation errors (400); generic message otherwise
        const detail = error.response?.data?.detail;
        if (error.response?.status === 400 && detail) {
          toast.error(detail);
        } else {
          toast.error('Başvuru gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.');
        }
      }
    } finally {
      clearTimeout(slowTimer);
      clearTimeout(abortTimer);
      inFlightRef.current = false;
      setLoading(false);
      setLoadingMessage('');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-section)' }}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16 md:py-20">
          <div className="max-w-xl w-full mx-auto text-center bg-white border rounded-xl shadow-sm p-6 md:p-10" style={{ borderColor: 'var(--border-soft)' }}>
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: 'var(--green-main)' }}>
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-6" style={{ color: 'var(--text-primary)' }} data-testid="submission-success-title">
              Başvurunuz Alındı
            </h2>

            {referenceNumber && (
              <div className="rounded-lg px-5 py-4 mb-6 border-2" style={{ background: 'var(--bg-accent-soft)', borderColor: 'var(--green-main)' }} data-testid="reference-block">
                <p className="text-[11px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary)' }}>Referans Numaranız</p>
                <p className="text-xl md:text-2xl font-mono font-black break-all" style={{ color: 'var(--green-main)' }} data-testid="reference-number">{referenceNumber}</p>
              </div>
            )}

            <p className="text-[15px] font-semibold leading-relaxed mb-3" style={{ color: 'var(--text-primary)' }} data-testid="success-next-1">
              Ekibimiz başvurunuzu inceleyecek ve 24 saat içinde ödeme bilgilerinizi WhatsApp üzerinden iletecektir.
            </p>
            <p className="text-sm font-medium leading-relaxed mb-8" style={{ color: 'var(--text-secondary-strong)' }} data-testid="success-next-2">
              Ödeme onaylandıktan sonra kargo adresi ve süreç detayları size otomatik olarak iletilecektir.
            </p>

            {whatsappSettings.number && (
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3.5 rounded-lg font-bold text-sm tracking-wide hover:bg-[#1fb855] transition shadow-md"
                data-testid="whatsapp-cta-button">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp ile İletişime Geç
              </a>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" data-testid="submission-title">Satıcılar İçin Ürün Test Başvurusu</h2>
        <p className="text-base text-neutral-500 mb-12 leading-relaxed">Ürününüzü bağımsız değerlendirme için gönderin. Test sonuçları ne olursa olsun yayınlanacaktır.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="brand">Marka Adı *</label>
              <input type="text" id="brand" name="brand" value={formData.brand} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-brand" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="product_name">Ürün Adı *</label>
              <input type="text" id="product_name" name="product_name" value={formData.product_name} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-product-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="business_name">İşletme Adı *</label>
              <input type="text" id="business_name" name="business_name" value={formData.business_name} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-business-name" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="category">Kategori *</label>
              <select id="category" name="category" value={formData.category} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-category">
                <option value="">Kategori Seçin</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.name} value={cat.name} disabled={cat.disabled}>{cat.name}{cat.disabled ? ' (Yakında)' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="description">Açıklama * <span className="font-normal text-neutral-400">(min. 50 karakter)</span></label>
            <textarea id="description" name="description" value={formData.description} onChange={handleChange} required rows="4" minLength={50}
              className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-description" />
            <p className="text-xs text-neutral-400 mt-1">{formData.description.length}/50 karakter</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="address">Adres *</label>
              <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-address" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="city">Şehir *</label>
              <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required
                className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-city" />
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t border-neutral-200 pt-6 mt-8">
            <h3 className="text-xl font-black tracking-tight mb-6">İletişim Bilgileri</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold mb-2" htmlFor="contact_name">Ad Soyad *</label>
                <input type="text" id="contact_name" name="contact_name" value={formData.contact_name} onChange={handleChange} required
                  className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-contact-name" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold mb-2" htmlFor="contact_phone">Telefon * <span className="font-normal text-neutral-400">(5XXXXXXXXX)</span></label>
                  <input type="tel" id="contact_phone" name="contact_phone" value={formData.contact_phone}
                    onChange={handleChange} required placeholder="5XXXXXXXXX" maxLength={10}
                    className={`w-full border px-4 py-3 rounded-md focus:outline-none transition ${phoneError ? 'border-red-400 focus:border-red-500' : 'border-neutral-300 focus:border-[#1C3B2A]'}`}
                    data-testid="input-contact-phone" />
                  {phoneError && <p className="text-xs text-red-500 mt-1" data-testid="phone-error">{phoneError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2" htmlFor="contact_email">E-posta <span className="font-normal text-neutral-400">(opsiyonel)</span></label>
                  <input type="email" id="contact_email" name="contact_email" value={formData.contact_email} onChange={handleChange}
                    className="w-full border border-neutral-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#1C3B2A] transition" data-testid="input-contact-email" />
                </div>
              </div>
            </div>
          </div>

          {/* 4 Checkboxes */}
          <div className="border-t border-neutral-200 pt-6 mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <input type="checkbox" id="kvkk_accepted" name="kvkk_accepted" checked={formData.kvkk_accepted} onChange={handleChange}
                className="mt-1 w-4 h-4 accent-[#1C3B2A]" data-testid="checkbox-kvkk" />
              <label htmlFor="kvkk_accepted" className="text-sm text-neutral-700">KVKK kapsamında kişisel verilerimin işlenmesine onay veriyorum. *</label>
            </div>
            <div className="flex items-start gap-3">
              <input type="checkbox" id="process_irreversible_accepted" name="process_irreversible_accepted"
                checked={formData.process_irreversible_accepted} onChange={handleChange}
                className="mt-1 w-4 h-4 accent-[#1C3B2A]" data-testid="checkbox-process" />
              <label htmlFor="process_irreversible_accepted" className="text-sm text-neutral-700">Test süreci başladıktan sonra geri alınamayacağını kabul ediyorum. *</label>
            </div>
            <div className="flex items-start gap-3">
              <input type="checkbox" id="product_handling_accepted" name="product_handling_accepted"
                checked={formData.product_handling_accepted} onChange={handleChange}
                className="mt-1 w-4 h-4 accent-[#1C3B2A]" data-testid="checkbox-handling" />
              <label htmlFor="product_handling_accepted" className="text-sm text-neutral-700">Ürünün test sırasında açılabileceğini, sökülebileceğini veya iade edilmeyebileceğini kabul ediyorum. *</label>
            </div>
            <div className="flex items-start gap-3">
              <input type="checkbox" id="results_final_accepted" name="results_final_accepted"
                checked={formData.results_final_accepted} onChange={handleChange}
                className="mt-1 w-4 h-4 accent-[#1C3B2A]" data-testid="checkbox-results" />
              <label htmlFor="results_final_accepted" className="text-sm text-neutral-700">
                Test sonuçlarının değiştirilemeyeceğini veya kaldırılamayacağını kabul ediyorum. *
                {' '}<button type="button" onClick={() => setShowLegalModal(true)}
                  className="underline font-semibold text-[#1C3B2A] hover:text-[#152e21] transition"
                  data-testid="legal-link">Yasal şartları</button> okudum ve kabul ediyorum.
              </label>
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-[#1C3B2A] text-white px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#152e21] transition disabled:opacity-60 disabled:cursor-not-allowed rounded-md"
            data-testid="submit-button" aria-live="polite">
            {loading ? (loadingMessage || 'Başvuru gönderiliyor...') : 'SATICILAR İÇİN ÜRÜN TEST BAŞVURUSU GÖNDER'}
          </button>
        </form>
      </div>

      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4" data-testid="legal-modal-overlay"
          onClick={() => setShowLegalModal(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="legal-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-black tracking-tight" data-testid="legal-modal-title">Yasal Şartlar ve Koşullar</h3>
              <button onClick={() => setShowLegalModal(false)} className="p-1 hover:bg-neutral-100 rounded transition" data-testid="legal-modal-close">
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1" ref={legalContentRef} onScroll={handleLegalScroll}>
              <div className="prose prose-sm max-w-none text-neutral-700 leading-relaxed">
                {LEGAL_TEXT.split('\n\n').map((paragraph, i) => (
                  <p key={i} className={`mb-4 text-sm leading-relaxed ${paragraph === paragraph.toUpperCase() && paragraph.length > 50 ? 'font-bold text-[#111]' : ''}`}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-200">
              {!scrolledToBottom && (
                <p className="text-xs text-neutral-400 text-center mb-3">Kabul etmek için lütfen metni sonuna kadar okuyun.</p>
              )}
              <button onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    kvkk_accepted: true, process_irreversible_accepted: true,
                    product_handling_accepted: true, results_final_accepted: true,
                  }));
                  setShowLegalModal(false);
                }}
                disabled={!scrolledToBottom}
                className={`w-full px-6 py-3 font-bold text-sm rounded transition ${scrolledToBottom ? 'bg-[#1C3B2A] text-white hover:bg-[#152e21]' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`}
                data-testid="legal-accept-button">
                Okudum ve Kabul Ediyorum
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SubmissionPage;
